import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Heart, Menu, X, Linkedin, Instagram, Facebook, Youtube, LogIn, LogOut, User } from 'lucide-react';
import Logo from './Logo';
import { useFavorites } from '../context/FavoritesContext';
import { useAuth, startGoogleLogin } from '../context/AuthContext';

const NAV = [
  { to: '/', label: 'Accueil' },
  { to: '/catalogue', label: 'Explorer les webtoons' },
  { to: '/collections', label: 'Collections' },
  { to: '/auteurs', label: 'Auteurs et autrices' },
  { to: '/actualites', label: 'Actualités' },
  { to: '/apropos', label: 'À propos' },
];

const Header = () => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { favorites } = useFavorites();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/catalogue?q=${encodeURIComponent(q.trim())}`);
    setOpen(false);
  };

  const isActive = (to) => (to === '/' ? location.pathname === '/' : location.pathname.startsWith(to));

  return (
    <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-md border-b border-border">
      {/* Social strip */}
      <div className="hidden md:flex justify-center gap-2 py-1.5" style={{ background: 'linear-gradient(90deg,#ed5c1f,#ff8a3d)' }}>
        {[Linkedin, Instagram, Facebook, Youtube].map((Icon, i) => (
          <a key={i} href="https://www.youtube.com/channel/UCQy5OVfMCOYSKtuRqQQ22Ag" target="_blank" rel="noreferrer"
             className="w-6 h-6 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center transition-colors">
            <Icon className="w-3.5 h-3.5 text-white" />
          </a>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
        <Logo />

        <nav className="hidden lg:flex items-center gap-7 ml-auto">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to}
              className={`link-underline font-700 text-[15px] transition-colors ${isActive(n.to) ? 'text-primary active' : 'text-foreground hover:text-primary'}`}>
              {n.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={submit} className="hidden md:flex items-center ml-auto lg:ml-4 relative">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Un mot, un sujet ?"
            className="w-44 lg:w-52 pl-4 pr-10 py-2 rounded-full bg-muted text-sm font-600 outline-none focus:ring-2 focus:ring-primary/40 transition-all" />
          <button type="submit" className="absolute right-1 w-8 h-8 rounded-full bg-primary flex items-center justify-center hover:bg-primary/90 transition-colors">
            <Search className="w-4 h-4 text-white" />
          </button>
        </form>

        <Link to="/favoris" className="relative w-10 h-10 rounded-full bg-muted hover:bg-secondary/30 flex items-center justify-center transition-colors">
          <Heart className="w-5 h-5 text-primary" />
          {favorites.length > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-white text-[10px] font-800 flex items-center justify-center">{favorites.length}</span>
          )}
        </Link>

        {user ? (
          <div className="relative hidden sm:block">
            <button onClick={() => setMenuOpen((v) => !v)} className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-muted hover:bg-secondary/30 transition-colors">
              {user.picture ? <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" referrerPolicy="no-referrer" /> : <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center"><User className="w-4 h-4 text-white" /></div>}
              <span className="font-700 text-sm max-w-[100px] truncate">{user.name?.split(' ')[0]}</span>
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-52 bg-card border border-border rounded-2xl shadow-xl p-2 animate-float-up">
                <div className="px-3 py-2 border-b border-border mb-1">
                  <p className="font-700 text-sm truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                <Link to="/favoris" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted font-700 text-sm"><Heart className="w-4 h-4 text-primary" /> Mes favoris</Link>
                <button onClick={() => { setMenuOpen(false); logout(); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-destructive hover:text-white font-700 text-sm transition-colors"><LogOut className="w-4 h-4" /> Déconnexion</button>
              </div>
            )}
          </div>
        ) : (
          <button onClick={startGoogleLogin} className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-white font-800 text-sm hover:scale-105 transition-transform shadow-lg shadow-primary/20">
            <LogIn className="w-4 h-4" /> Connexion
          </button>
        )}

        <button className="lg:hidden w-10 h-10 rounded-full bg-muted flex items-center justify-center" onClick={() => setOpen(!open)}>
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background px-4 py-4 space-y-1 animate-float-up">
          <form onSubmit={submit} className="flex items-center relative mb-3">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Un mot, un sujet ?"
              className="w-full pl-4 pr-10 py-2.5 rounded-full bg-muted text-sm font-600 outline-none" />
            <button type="submit" className="absolute right-1 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <Search className="w-4 h-4 text-white" />
            </button>
          </form>
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
              className={`block px-3 py-2.5 rounded-lg font-700 ${isActive(n.to) ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'}`}>
              {n.label}
            </Link>
          ))}
          {user ? (
            <button onClick={() => { setOpen(false); logout(); }} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg font-700 text-foreground hover:bg-muted"><LogOut className="w-4 h-4" /> Déconnexion ({user.name?.split(' ')[0]})</button>
          ) : (
            <button onClick={() => { setOpen(false); startGoogleLogin(); }} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg font-800 bg-primary text-white"><LogIn className="w-4 h-4" /> Connexion</button>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
